/**
 * Generate a cryptographically random nonce for CSP
 * @returns {string} A random base64 encoded nonce
 */
function generateRandomNonce() {
  // Generate a random array of bytes
  const array = new Uint8Array(16); // 16 bytes = 128 bits of randomness
  window.crypto.getRandomValues(array);
  
  // Convert to base64
  return btoa(Array.from(array).map(b => String.fromCharCode(b)).join(''))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * Simple utility to get the CSP nonce value from the meta tag
 * @returns {string} The CSP nonce or a generated nonce if none found
 */
export function getCspNonce() {
  // First check if we have a stored session nonce
  const storedNonce = sessionStorage.getItem('app-csp-nonce');
  if (storedNonce) {
    return storedNonce;
  }

  // Otherwise get it from the meta tag
  const nonceMetaTag = document.querySelector('meta[name="csp-nonce"]');
  const metaNonce = nonceMetaTag ? nonceMetaTag.getAttribute('content') : null;

  // If we found a nonce in the meta tag and it's not the placeholder, store and use it
  if (metaNonce && metaNonce !== 'CSP_NONCE') {
    // Store in sessionStorage for consistent use across page loads in this session
    try {
      sessionStorage.setItem('app-csp-nonce', metaNonce);
    } catch (e) {
      // Ignore storage errors (private browsing etc.)
      console.warn('Failed to store CSP nonce:', e);
    }
    console.log('Nonce from meta tag:', metaNonce);
    return metaNonce;
  }

  // Check for header-based nonce
  const headerNonce = window.responseHeaders && window.responseHeaders['X-CSP-Nonce'];
  if (headerNonce) {
    try {
      sessionStorage.setItem('app-csp-nonce', headerNonce);
    } catch (e) {
      console.warn('Failed to store header nonce:', e);
    }
    console.log('Nonce from header:', headerNonce);
    return headerNonce;
  }

  // If we don't have a valid nonce, generate one for this session
  // This is a fallback for development/testing when server doesn't set the nonce
  const generatedNonce = generateRandomNonce();
  console.log('Generated fallback nonce:', generatedNonce);
  try {
    sessionStorage.setItem('app-csp-nonce', generatedNonce);
  } catch (e) {
    console.warn('Failed to store generated nonce:', e);
  }
  return generatedNonce;
}

/**
 * Apply CSP nonce to an element (typically script or style)
 * @param {HTMLElement} element - The DOM element to apply the nonce to
 * @returns {HTMLElement} The same element with nonce applied
 */
export function applyNonce(element) {
  const nonce = getCspNonce();
  if (nonce && element) {
    element.setAttribute('nonce', nonce);
  }
  return element;
}

/**
 * Get an object with nonce attribute for inline styles
 * @returns {Object} Object with nonce attribute for inline styles
 */
export function getNonceForStyle() {
  const nonce = getCspNonce();
  return nonce ? { nonce } : {};
}

/**
 * Create a script element with CSP nonce applied
 * @param {Object} options - Script options
 * @param {string} options.src - Optional source URL
 * @param {string} options.content - Optional inline script content
 * @param {boolean} options.async - Whether to load script asynchronously
 * @param {Function} options.onLoad - Optional onload callback
 * @returns {HTMLScriptElement} The created script element
 */
export function createScript({ src, content, async = true, onLoad }) {
  const script = document.createElement('script');
  applyNonce(script);
  
  if (src) {
    script.src = src;
  }
  
  if (content) {
    script.textContent = content;
  }
  
  if (async) {
    script.async = true;
  }
  
  if (onLoad) {
    script.onload = onLoad;
  }
  
  return script;
}

/**
 * Load a script dynamically with CSP nonce applied
 * @param {Object} options - Script options
 * @param {string} options.src - Source URL
 * @param {boolean} options.async - Whether to load script asynchronously
 * @param {Function} options.onLoad - Optional onload callback
 * @returns {Promise<void>} Promise resolving when script loads
 */
export function loadScript({ src, async = true, onLoad }) {
  return new Promise((resolve, reject) => {
    const script = createScript({ 
      src, 
      async,
      onLoad: () => {
        if (onLoad) onLoad();
        resolve();
      }
    });
    
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

/**
 * Create a style element with CSP nonce applied
 * @param {string} cssText - The CSS content
 * @returns {HTMLStyleElement} The created style element
 */
export function createStyle(cssText) {
  const style = document.createElement('style');
  applyNonce(style);
  style.textContent = cssText;
  return style;
}

/**
 * Add nonce attribute to HTML string content (for use with dangerouslySetInnerHTML)
 * @param {string} htmlContent - HTML content that may contain script or style tags
 * @returns {string} HTML with nonce attributes added to scripts and styles
 */
export function addNonceToHtml(htmlContent) {
  if (!htmlContent) return '';
  
  const nonce = getCspNonce();
  if (!nonce) return htmlContent;
  
  // Add nonce to <script> tags that don't already have it
  let result = htmlContent.replace(/<script(?![^>]*nonce=)/gi, `<script nonce="${nonce}"`);
  
  // Add nonce to <style> tags that don't already have it
  result = result.replace(/<style(?![^>]*nonce=)/gi, `<style nonce="${nonce}"`);
  
  return result;
}

/**
 * Initialize CSP nonce handling as early as possible
 * This should be called in your app's entry point
 */
export function initializeCspNonce() {
  // Get nonce as early as possible (this will also store it in sessionStorage)
  const nonce = getCspNonce();
  
  if (!nonce) {
    console.error('[CSP] Failed to get or generate nonce. CSP protection may fail.');
    return nonce;
  }
  
  // Update the meta tag with the actual nonce (if it was generated)
  const nonceMetaTag = document.querySelector('meta[name="csp-nonce"]');
  if (nonceMetaTag && nonceMetaTag.getAttribute('content') === 'CSP_NONCE') {
    nonceMetaTag.setAttribute('content', nonce);
  }
  
  // Update any script or style tags with CSP_NONCE placeholders
  document.querySelectorAll('script[nonce="CSP_NONCE"], style[nonce="CSP_NONCE"]').forEach(el => {
    el.setAttribute('nonce', nonce);
  });
  
  // Make React aware of our CSP nonce
  if (window.__REACT_NONCE__ === undefined) {
    window.__REACT_NONCE__ = nonce;
  }
  
  // Also set webpack nonce for bundled styles
  if (window.__webpack_nonce__ === undefined) {
    window.__webpack_nonce__ = nonce;
  }
  
  // Log for debugging (remove in production)
  if (process.env.NODE_ENV !== 'production') {
    console.log('[CSP] Nonce initialized:', nonce.substring(0, 6) + '...');
  }
  
  // IMPORTANT: Apply the nonce to all existing script and style tags
  // This ensures that the page's initial scripts/styles have the correct nonce
  const allScripts = document.querySelectorAll('script:not([nonce])');
  const allStyles = document.querySelectorAll('style:not([nonce])');
  
  allScripts.forEach(script => script.setAttribute('nonce', nonce));
  allStyles.forEach(style => style.setAttribute('nonce', nonce));
  
  // Add observer to handle dynamically added scripts/styles
  if (window.MutationObserver) {
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.addedNodes && mutation.addedNodes.length > 0) {
          // Use the stored nonce for consistency
          const storedNonce = getCspNonce();
          
          if (!storedNonce) return;
          
          mutation.addedNodes.forEach(node => {
            // Apply nonce to new script or style elements
            if ((node.tagName === 'SCRIPT' || node.tagName === 'STYLE') && !node.getAttribute('nonce')) {
              node.setAttribute('nonce', storedNonce);
            }
          });
        }
      });
    });
    
    // Start observing only document.head and the main content container
    // This is much more performant than observing the entire document
    observer.observe(document.head, {
      childList: true,
      subtree: true
    });
    
    // Also observe the main content area if it exists
    // This targets common root element IDs used in React/SPA applications
    const mainContentSelectors = ['#root', '#app', '#main', '.app-container', '[data-app-root]'];
    const mainContentArea = document.querySelector(mainContentSelectors.join(','));
    
    if (mainContentArea) {
      observer.observe(mainContentArea, {
        childList: true,
        subtree: true
      });
    }
    
    // For style attribute changes, we'll use a separate, more targeted approach
    // instead of watching all attribute changes across the entire document
    const styleAttrObserver = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          const node = mutation.target;
          const storedNonce = getCspNonce();
          
          if (storedNonce && !node.getAttribute('nonce')) {
            node.setAttribute('nonce', storedNonce);
          }
        }
      });
    });
    
    // Only observe style attributes on elements that commonly get dynamic styling
    document.querySelectorAll('[style], .dynamic-styled, [data-has-dynamic-style]').forEach(el => {
      styleAttrObserver.observe(el, {
        attributes: true,
        attributeFilter: ['style']
      });
    });
  }
  
// For compatibility with older browsers or if MutationObserver isn't available,
  // also monkey-patch createElement to automatically add nonce to scripts/styles
  const originalCreateElement = document.createElement;
  document.createElement = function() {
    const element = originalCreateElement.apply(document, arguments);
    try {
      if (arguments[0].toLowerCase() === 'script' || arguments[0].toLowerCase() === 'style') {
        element.setAttribute('nonce', getCspNonce());
      }
    } catch (error) {
      console.warn('[CSP] Error adding nonce to element:', error);
      // Continue with the element creation even if nonce application fails
    }
    return element;
  };
  
  return nonce;
}
